import React, {HTMLAttributeAnchorTarget, useCallback, useMemo} from 'react';
import Icon, {IconNameType} from "components/atom/Icon.tsx";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {useMount} from "react-use";
import {UserType} from "globals/types/models.ts";
import {useAppDispatch, useAppSelector} from "config/store";
import {authActions} from "config/store/modules/auth.ts";
import {resetSessionId} from "config/ajax.ts";
import {useTranslation} from "react-i18next";
import {STORAGE_WELCOME_MODAL_KEY} from "utils/data/global.ts";
import  "utils/admin/bootstrapChartJs.ts";

function AdminConnectedContainer(props: { children: React.ReactNode, title: string, rightTitle?: React.ReactNode }) {
    const navigate = useNavigate()
    const location = useLocation()
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const currentUser: UserType | null = useAppSelector(state => state.auth.user)

    useMount(() => {
        //go to admin page if we are not in admin page
        if (currentUser?.isAdmin) {
            if (!location.pathname.startsWith('/profile/admin')) {
                navigate('/profile/admin')
            }
        }
    })

    const showGoBack = useMemo(() => {
        if (location.pathname === '/profile/admin') {
            return false
        }

        return true
    }, [location.pathname])

    const onClickGoBack = useCallback(() => {
        if (location.pathname.startsWith('/profile/admin/events/')) {
            navigate('/profile/admin/events')
            return
        }
        if (location.pathname.startsWith('/profile/admin/users/')) {
            navigate('/profile/admin/users')
            return
        }

        navigate('/profile/admin')
    }, [location.pathname, navigate])

    const logout = useCallback((e: any) => {
        e.preventDefault()
        dispatch(authActions.setJwt(null))
        dispatch(authActions.setUser(null))
        resetSessionId()
        window.localStorage.removeItem(STORAGE_WELCOME_MODAL_KEY)
        navigate({pathname: '/map', hash: '#/auth/login'})
    }, [dispatch, navigate])

    return (
        <div className="flex h-screen w-full flex-col items-start justify-start overflow-hidden">
            <div className="flex w-full flex-row items-center justify-center bg-red-500">
                <p className="py-1 text-xs font-medium text-white">ADMIN</p>
            </div>
            <div className="flex h-full w-full flex-1 flex-row items-stretch justify-center ">
                <div className="w-[225px] min-w-[225px] border-r border-gray-100">
                    <div className="w-full pt-12">
                        <div className="flex w-full flex-col items-center justify-start gap-4 px-2">
                            <CustomNavLink end icon="dashboard" label={t('menu.admin.dashboard')} to="/profile/admin"/>
                            <CustomNavLink icon="events" label={t('menu.admin.events')} to="/profile/admin/events"/>
                            <CustomNavLink icon="users" label={t('menu.admin.users')} to="/profile/admin/users"/>
                            <CustomNavLink icon="share" label={t('menu.admin.notifications')} to="/profile/admin/notifications"/>
                            <CustomNavLink end icon="map" label={t('menu.admin.map')} target="_blank" to="/map"/>
                            <CustomNavLink onClick={logout} icon="arrow-turn-down-left" label={t('menu.admin.logout')}
                                           to="/profile/logout"/>
                        </div>
                    </div>
                </div>
                <div className="flex w-full flex-col items-start justify-start px-2 pt-12">
                    <div className="container mx-auto">
                        <div className="flex flex-row flex-nowrap items-center justify-center">
                            <div className="flex w-24 flex-row items-center justify-start gap-4">
                                {showGoBack && (
                                    <button type="button"
                                            className="flex appearance-none flex-row items-center justify-center gap-1"
                                            onClick={onClickGoBack}>
                                        <Icon name="angle-left" className="block h-6 w-6 text-gray-900"/>
                                        <span>
                                            {t('goBack')}
                                        </span>
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-1 flex-row items-center justify-center gap-4">
                                <h2 className="text-xl">
                                    {props.title}
                                </h2>
                            </div>
                            <div className="flex w-24 flex-row items-center justify-end gap-4">
                                {props.rightTitle}
                            </div>
                        </div>
                    </div>
                    <div className="flex max-h-full w-full flex-1 flex-col">
                        <div className="flex h-full w-full flex-1 flex-col overflow-y-scroll pb-40 pt-12">
                            <div className="container mx-auto flex w-full flex-1 flex-col">
                                {props.children}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

}

function CustomNavLink(props: {
    to: string,
    target?: HTMLAttributeAnchorTarget,
    icon: IconNameType,
    label: string,
    end?: boolean,
    onClick?: (e: any) => void
}) {
    const {to, icon, label, end = false, target = '_self'} = props
    return (
        <NavLink
            end={end}
            className={({isActive}) => {
                let str = 'w-full p-2 rounded-md flex flex-row justify-center items-center gap-3'
                if (isActive) {
                    str += ' bg-green-150 border border-green-500'
                } else {
                    str += '  border border-gray-100'
                }
                return str
            }
            }
            to={to}
            target={target}
            onClick={props.onClick}
        >
            <Icon name={icon} className="h-6 w-6 min-w-6 text-gray-700"/>
            <span>
                {label}
            </span>
        </NavLink>
    )
}

export default AdminConnectedContainer;
